import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, HashRouter } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { ProductGrid } from './components/ProductGrid';
import { Testimonials } from './components/Testimonials';
import { LearnGrow } from './components/LearnGrow';
import { Footer } from './components/Footer';
import { SellWithUsPage } from './pages/SellWithUsPage';
import { SellerDashboard } from './pages/SellerDashboard';
import { BuyerDashboard } from './pages/BuyerDashboard';
import { SellProductPage } from './pages/SellProductPage';
import { AuthProvider } from './components/auth/AuthProvider';
import { TrustSection } from './components/TrustSection';
import { ThemeProvider } from './components/ThemeProvider';
import { CategoryGrid } from './components/CategoryGrid';
import { BrowseProductsPage } from './pages/BrowseProductsPage';
import { SubmitProductPage } from './pages/SubmitProductPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutConfirmationPage } from './pages/CheckoutConfirmationPage';
import { PricingStrategiesPage } from './pages/learning/PricingStrategiesPage';
import { ProductDescriptionsPage } from './pages/learning/ProductDescriptionsPage';
import { ProductBundlesPage } from './pages/learning/ProductBundlesPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsPage } from './pages/TermsPage';
import { ProductCategoryPage } from './pages/ProductCategoryPage';
import { SubscribePage } from './pages/SubscribePage';
import { SellerProfilePage } from './pages/SellerProfilePage';
import { ScrollToTop } from './components/ScrollToTop';
import { CartProvider } from './components/cart/CartContext';
import { CartSidebar } from './components/cart/CartSidebar';
import { SettingsPage } from './pages/SettingsPage';
import { EmailSupport } from './pages/support/EmailSupport';
import { LiveChatSupport } from './pages/support/LiveChatSupport';
import { AccountSettingsLayout } from './pages/account-settings/AccountSettingsLayout';
import { ProfileSection } from './pages/account-settings/ProfileSection';
import { StoreSettingsSection } from './pages/account-settings/StoreSettingsSection';
import { PaymentsPayoutsSection } from './pages/account-settings/PaymentsPayoutsSection';
import { ProductPreferencesSection } from './pages/account-settings/ProductPreferencesSection';
import { NotificationPreferencesSection } from './pages/account-settings/NotificationPreferencesSection';
import { SecuritySection } from './pages/account-settings/SecuritySection';
import { SupportFeedbackSection } from './pages/account-settings/SupportFeedbackSection';
import { AccountActionsSection } from './pages/account-settings/AccountActionsSection';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import SecurityMonitoringPage from './pages/security/SecurityMonitoringPage';
import SecurityPostureDashboard from './pages/security/SecurityPostureDashboard';
import PredictiveThreatDashboard from './pages/security/PredictiveThreatDashboard';
// Detect if we should use hash routing
// This helps with static hosting platforms that don't support proper URL rewriting
const useHashRouter = window.location.href.includes('use-hash-router=true') || process.env.NODE_ENV === 'production';
// HomePage defined outside App to prevent unmount/remount on re-renders
const HomePage: React.FC<{
  scrolled: boolean;
}> = ({
  scrolled
}) => <div className="w-full bg-white dark:bg-[#0F1F1D] overflow-x-hidden transition-colors duration-300" data-id="element-0">
    <Navbar scrolled={scrolled} data-id="element-1" />
    <main data-id="element-2">
      <Hero data-id="element-3" />
      <CategoryGrid data-id="element-4" />
      <TrustSection data-id="element-5" />
      <Testimonials data-id="element-6" />
      <LearnGrow data-id="element-7" />
    </main>
    <Footer data-id="element-8" />
  </div>;
export function App() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Define routes to be used in both router types
  const appRoutes = <Routes data-id="element-9">
      <Route path="/" element={<HomePage scrolled={scrolled} data-id="element-11" />} data-id="element-10" />
      <Route path="/sell-with-us" element={<SellWithUsPage data-id="element-13" />} data-id="element-12" />
      {/* Protected Seller Routes */}
      <Route path="/seller-dashboard" element={<ProtectedRoute requiredRoles={['seller']} showAccessDenied data-id="element-15">
            <SellerDashboard data-id="element-16" />
          </ProtectedRoute>} data-id="element-14" />
      <Route path="/buyer-dashboard" element={<ProtectedRoute requireAuth data-id="element-18">
            <BuyerDashboard data-id="element-19" />
          </ProtectedRoute>} data-id="element-17" />
      <Route path="/sell-product" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-21">
            <SellProductPage data-id="element-22" />
          </ProtectedRoute>} data-id="element-20" />
      <Route path="/submit" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-24">
            <SubmitProductPage data-id="element-25" />
          </ProtectedRoute>} data-id="element-23" />
      {/* Public Product Routes */}
      <Route path="/browse" element={<BrowseProductsPage data-id="element-27" />} data-id="element-26" />
      <Route path="/product/:productId" element={<ProductDetailPage data-id="element-29" />} data-id="element-28" />
      {/* Checkout route */}
      <Route path="/checkout" element={<ProtectedRoute requireAuth data-id="element-31">
            <CheckoutPage data-id="element-32" />
          </ProtectedRoute>} data-id="element-30" />
      {/* Protected Settings Routes */}
      <Route path="/settings" element={<ProtectedRoute requireAuth data-id="element-34">
            <SettingsPage data-id="element-35" />
          </ProtectedRoute>} data-id="element-33" />
      {/* Account Settings pages - all require authentication */}
      <Route path="/account-settings" element={<ProtectedRoute requireAuth data-id="element-37">
            <AccountSettingsLayout data-id="element-38" />
          </ProtectedRoute>} data-id="element-36">
        <Route path="profile" element={<ProfileSection data-id="element-40" />} data-id="element-39" />
        <Route path="store" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-42">
              <StoreSettingsSection data-id="element-43" />
            </ProtectedRoute>} data-id="element-41" />
        <Route path="payments" element={<ProtectedRoute requiredRoles={['seller']} data-id="element-45">
              <PaymentsPayoutsSection data-id="element-46" />
            </ProtectedRoute>} data-id="element-44" />
        <Route path="product" element={<ProductPreferencesSection data-id="element-48" />} data-id="element-47" />
        <Route path="notifications" element={<NotificationPreferencesSection data-id="element-50" />} data-id="element-49" />
        <Route path="security" element={<SecuritySection data-id="element-52" />} data-id="element-51" />
        <Route path="support" element={<SupportFeedbackSection data-id="element-54" />} data-id="element-53" />
        <Route path="actions" element={<AccountActionsSection data-id="element-56" />} data-id="element-55" />
      </Route>

      {/* Security Monitoring - Admin only */}
      <Route path="/security-monitoring" element={<ProtectedRoute requiredRoles={['admin']} showAccessDenied data-id="element-58">
            <SecurityMonitoringPage data-id="element-59" />
          </ProtectedRoute>} data-id="element-57" />

      {/* Security Posture Dashboard - Admin only */}
      <Route path="/security-posture" element={<ProtectedRoute requiredRoles={['admin']} showAccessDenied data-id="element-61">
            <SecurityPostureDashboard data-id="element-62" />
          </ProtectedRoute>} data-id="element-60" />

      {/* Predictive Threat Dashboard - Admin only */}
      <Route path="/predictive-threats" element={<ProtectedRoute requiredRoles={['admin']} showAccessDenied data-id="element-64">
            <PredictiveThreatDashboard data-id="element-65" />
          </ProtectedRoute>} data-id="element-63" />

      {/* Learning pages */}
      <Route path="/learn/pricing-strategies" element={<PricingStrategiesPage data-id="element-67" />} data-id="element-66" />
      <Route path="/learn/product-descriptions" element={<ProductDescriptionsPage data-id="element-69" />} data-id="element-68" />
      <Route path="/learn/product-bundles" element={<ProductBundlesPage data-id="element-71" />} data-id="element-70" />
      {/* Footer pages */}

      <Route path="/about" element={<AboutPage data-id="element-73" />} data-id="element-72" />

      <Route path="/contact" element={<ContactPage data-id="element-75" />} data-id="element-74" />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-77" />} data-id="element-76" />
      <Route path="/terms" element={<TermsPage data-id="element-79" />} data-id="element-78" />

      {/* Support pages */}
      <Route path="/support/email" element={<EmailSupport data-id="element-81" />} data-id="element-80" />
      <Route path="/support/chat" element={<LiveChatSupport data-id="element-83" />} data-id="element-82" />
      {/* Product category pages */}
      <Route path="/products/:category" element={<ProductCategoryPage data-id="element-85" />} data-id="element-84" />

      {/* Subscription pages */}
      <Route path="/subscribe/:planId" element={<ProtectedRoute requireAuth data-id="element-87">
            <SubscribePage data-id="element-88" />
          </ProtectedRoute>} data-id="element-86" />
      {/* Seller profile page */}
      <Route path="/seller/:sellerId" element={<SellerProfilePage data-id="element-90" />} data-id="element-89" />
    </Routes>;
  return <ThemeProvider data-id="element-91">
      <AuthProvider data-id="element-92">
        <CartProvider data-id="element-93">
          {useHashRouter ?
        // Use HashRouter for production or when explicitly requested
        <HashRouter data-id="element-94">
              <ScrollToTop data-id="element-95" />
              {appRoutes}
              <CartSidebar data-id="element-96" />
            </HashRouter> :
        // Use BrowserRouter for development
        <BrowserRouter data-id="element-97">
              <ScrollToTop data-id="element-98" />
              {appRoutes}
              <CartSidebar data-id="element-99" />
            </BrowserRouter>}
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>;
}